/**
 * Patient module theme — light blue–purple (Kornivus soft brand).
 *
 * Change colors HERE only; tabs, sections, and action buttons across the
 * patient module resolve through these tokens.
 *
 * Status pills (Admit, Active, Pre-Admit, Hold, Inactive, Discharged, Non-Admit, Deceased) stay in StatusBadge.jsx
 * and must NOT use this theme.
 */

/** Brand hex (light blue ↔ soft purple). */
export const PATIENT_THEME_HEX = {
  /** Soft brand wash */
  brand: "#A5B4FC",
  /** Soft wash behind section headers */
  soft: "#EEF2FF",
  /** Borders around sections / chips */
  border: "#C7D2FE",
  /** Primary button fill */
  strong: "#4F46E5",
  /** Button labels & links — dark enough to read on white */
  text: "#4338CA",
  /** Section titles */
  title: "#312E81",
  /** Soft hover fill */
  softHover: "#E0E7FF",
};

/**
 * Tailwind utility tokens — light indigo (blue–purple).
 * Keep in sync with PATIENT_THEME_HEX when adjusting brand.
 */
export const PATIENT_THEME = {
  /** Collapsible / demographics section cards */
  section: {
    wrap: "border-indigo-200",
    head: "bg-indigo-50 border-indigo-100",
    bar: "bg-indigo-300",
    icon: "text-indigo-400",
    title: "text-indigo-800",
    edit: "text-indigo-700 border-indigo-300 hover:bg-indigo-50",
    addTone: "text-indigo-700 border-indigo-300 hover:bg-indigo-50",
    /** Light fill with dark text — white on indigo-300 was unreadable. */
    badge: "bg-indigo-100 text-indigo-700",
  },

  /** Patient summary sidebar section headers */
  summary: {
    bar: "bg-indigo-300",
    icon: "text-indigo-400",
    head: "bg-indigo-50/90",
    title: "text-indigo-800",
  },

  /**
   * List/table header rows. `className` is the ready-made string for the plain
   * tables in the module; the parts are exposed for the shared Table component,
   * which composes row and cell classes separately.
   */
  tableHead: {
    className:
      "bg-indigo-50/70 border-indigo-100 text-indigo-700",
    row: "bg-indigo-50/70 border-indigo-100",
    cell: "text-indigo-700",
    cellHover: "hover:bg-indigo-100/70",
    sortActive: "text-indigo-600",
  },

  /** Detail page top tabs */
  tab: {
    active: "text-indigo-600 bg-white",
    inactive: "text-slate-600 hover:text-slate-900 hover:bg-slate-100/80",
    underline: "bg-indigo-300",
  },

  /**
   * Button label text carries meaning, so these shades are chosen for contrast
   * against their own fill (>= 4.5:1), not to match the soft section washes
   * above. The lighter brand shades stay on bars, borders, and icons.
   */
  button: {
    primary:
      "bg-indigo-600 text-white hover:bg-indigo-700 border border-indigo-600",
    outline: "text-indigo-700 border-indigo-300 bg-white hover:bg-indigo-50",
    link: "text-indigo-700 hover:underline",
    soft: "bg-indigo-50 text-indigo-700 border border-indigo-300 hover:bg-indigo-100 hover:border-indigo-400",
  },

  focus: "focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100",
  fieldFocus: "focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100",
  menuHover: "hover:bg-indigo-50",
  accentSoft: "bg-indigo-50 border-indigo-100 text-indigo-800",
  chip: "text-indigo-600 bg-indigo-50 border border-indigo-100",
  modalHead: "border-indigo-100 bg-indigo-50/70",
  modalTitle: "text-indigo-800",
};

/** Staff role cards — same brand (no per-role rainbow). */
export const PATIENT_ROLE_TONE = {
  card: "border-indigo-200",
  head: "bg-indigo-50 border-indigo-100",
  bar: "bg-indigo-300",
  title: "text-indigo-800",
  badge: "bg-indigo-100 text-indigo-700",
  soft: "bg-indigo-50/40",
};

/** Default section tone used everywhere in the patient module. */
export const PATIENT_SECTION_TONE = PATIENT_THEME.section;

/**
 * Named section keys all resolve to the same theme tone
 * so existing `tone="diagnosis"` call sites keep working.
 */
export function patientSectionTone(_name) {
  return PATIENT_THEME.section;
}

/** Build a SECTION_TONES map where every key shares the patient theme. */
export function patientSectionTones(keys = []) {
  const tone = PATIENT_THEME.section;
  return Object.fromEntries(keys.map((key) => [key, tone]));
}

/** Inline CSS variables for optional custom styling on a patient shell. */
export function patientThemeCssVars() {
  const h = PATIENT_THEME_HEX;
  return {
    "--pt-brand": h.brand,
    "--pt-soft": h.soft,
    "--pt-border": h.border,
    "--pt-strong": h.strong,
    "--pt-text": h.text,
    "--pt-title": h.title,
    "--pt-soft-hover": h.softHover,
  };
}
